import React, { useState, useEffect, useRef, useCallback } from "react";
import { ZoomIn, ZoomOut, Check, Crop } from "lucide-react";
import { toast } from "sonner";

const CROP_BOX_SIZE = 280;
const OUTPUT_SIZE = 400;

export default function ImageCropModal({ imageSrc, onCancel, onApply }) {
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [maxScale, setMaxScale] = useState(3.5);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const modalRef = useRef(null);
  const triggerElementRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const applyButtonRef = useRef(null);
  const imgRef = useRef(null);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });

  // Focus trap & escape key management
  useEffect(() => {
    triggerElementRef.current = document.activeElement;
    applyButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }

      if (e.key === "Tab") {
        const focusable = [cancelButtonRef.current, applyButtonRef.current].filter(Boolean);
        if (focusable.length === 0) return;

        const firstEl = focusable[0];
        const lastEl = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (triggerElementRef.current && typeof triggerElementRef.current.focus === "function") {
        triggerElementRef.current.focus();
      }
    };
  }, [onCancel]);

  const clampPosition = useCallback((x, y, currentScale, img) => {
    if (!img) return { x: 0, y: 0 };
    const scaledWidth = img.naturalWidth * currentScale;
    const scaledHeight = img.naturalHeight * currentScale;

    const minX = CROP_BOX_SIZE - scaledWidth;
    const minY = CROP_BOX_SIZE - scaledHeight;

    return {
      x: Math.min(0, Math.max(minX, x)),
      y: Math.min(0, Math.max(minY, y)),
    };
  }, []);

  const onImageLoad = (e) => {
    const img = e.currentTarget;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;

    const initialScale = Math.max(CROP_BOX_SIZE / nw, CROP_BOX_SIZE / nh);
    setMinScale(initialScale);
    setMaxScale(initialScale * 4);
    setScale(initialScale);

    const initX = (CROP_BOX_SIZE - nw * initialScale) / 2;
    const initY = (CROP_BOX_SIZE - nh * initialScale) / 2;
    setPos({ x: initX, y: initY });
    setImgLoaded(true);
  };

  const handleZoomChange = (newScale) => {
    if (!imgRef.current) return;
    const img = imgRef.current;
    
    const centerX = CROP_BOX_SIZE / 2;
    const centerY = CROP_BOX_SIZE / 2;

    const imgPointX = (centerX - pos.x) / scale;
    const imgPointY = (centerY - pos.y) / scale;

    const nextX = centerX - imgPointX * newScale;
    const nextY = centerY - imgPointY * newScale;

    setScale(newScale);
    setPos(clampPosition(nextX, nextY, newScale, img));
  };

  const handlePointerDown = (e) => {
    setIsDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: pos.x,
      posY: pos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !imgRef.current) return;
    const dx = e.clientX - dragStartRef.current.mouseX;
    const dy = e.clientY - dragStartRef.current.mouseY;

    const newX = dragStartRef.current.posX + dx;
    const newY = dragStartRef.current.posY + dy;

    setPos(clampPosition(newX, newY, scale, imgRef.current));
  };

  const handlePointerUp = (e) => {
    setIsDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
  };

  const handleCrop = () => {
    if (!imgRef.current) return;
    const img = imgRef.current;

    const sourceX = Math.max(0, -pos.x / scale);
    const sourceY = Math.max(0, -pos.y / scale);
    const sourceWidth = Math.min(img.naturalWidth - sourceX, CROP_BOX_SIZE / scale);
    const sourceHeight = Math.min(img.naturalHeight - sourceY, CROP_BOX_SIZE / scale);

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      toast.error("Canvas context unavailable");
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      OUTPUT_SIZE,
      OUTPUT_SIZE
    );

    // canvas.toBlob() emits a clean binary Blob
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Could not process image crop");
          return;
        }
        onApply(blob);
      },
      "image/jpeg",
      0.88
    );
  };

  return (
    <div 
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="crop-modal-title"
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl flex flex-col items-center">
        <div className="w-full flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Crop className="w-4 h-4 text-blue-600" />
            <h3 id="crop-modal-title" className="text-sm font-bold text-zinc-950">Crop Profile Photo</h3>
          </div>
          <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-200">
            4:4 Ratio
          </span>
        </div>

        <div 
          className="relative rounded-2xl overflow-hidden border-2 border-zinc-200 bg-zinc-950 cursor-grab active:cursor-grabbing select-none"
          style={{ width: `${CROP_BOX_SIZE}px`, height: `${CROP_BOX_SIZE}px`, touchAction: "none" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt="Crop preview"
            onLoad={onImageLoad}
            className="absolute max-w-none pointer-events-none transition-transform duration-75 ease-out"
            style={{
              transform: `translate3d(${pos.x}px, ${pos.y}px, 0) scale(${scale})`,
              transformOrigin: "0 0",
              opacity: imgLoaded ? 1 : 0,
            }}
          />

          <div className="absolute inset-0 pointer-events-none border border-white/20">
            <div className="absolute inset-0 rounded-full border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 opacity-20">
              <div className="border-r border-b border-white" />
              <div className="border-r border-b border-white" />
              <div className="border-b border-white" />
              <div className="border-r border-b border-white" />
              <div className="border-r border-b border-white" />
              <div className="border-b border-white" />
              <div className="border-r border-white" />
              <div className="border-r border-white" />
              <div />
            </div>
          </div>
        </div>

        <p className="text-[11px] text-zinc-400 font-medium mt-3 text-center">
          Drag to center · Use slider to zoom
        </p>

        <div className="w-full flex items-center gap-2 mt-4 px-2">
          <ZoomOut className="w-4 h-4 text-zinc-400 shrink-0" />
          <input
            type="range"
            min={minScale}
            max={maxScale}
            step={(maxScale - minScale) / 100}
            value={scale}
            onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
            className="w-full h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            aria-label="Zoom level"
          />
          <ZoomIn className="w-4 h-4 text-zinc-400 shrink-0" />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full mt-6">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            className="h-11 px-4 bg-zinc-100 text-zinc-700 font-bold text-xs rounded-xl hover:bg-zinc-200 transition focus:ring-2 focus:ring-zinc-400"
          >
            Cancel
          </button>
          <button
            ref={applyButtonRef}
            type="button"
            onClick={handleCrop}
            className="h-11 px-4 bg-blue-600 text-white font-bold text-xs rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-1.5 shadow-xs focus:ring-2 focus:ring-blue-500"
          >
            <Check className="w-4 h-4" /> Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
}